import {
  getExpansionDashboard,
  getExpansionLookup,
  getExpansionSelect,
  getExpansionSignalTypes,
  getExpansionRecent,
  getExpansionSavedListActivity,
  getSavedSearchTypes,
} from "@/api/expansions";
import {
  getSaveList,
  addSaveList,
  updateSaveList,
  deleteSaveList,
} from "@/api/company";
import { MAP_MODE, MAX_VISIBLE_MARKETS, MAP_FLOWS_REQUEST_SIZE, TOP_FLOWS_RETRY_SIZE, flowsSizeForDensity, WIDE_EXPANDERS_MIN_MARKETS } from "@/views/home/components/Expansion/shared/taxonomy";
import { DEFAULT_WINDOW_DAYS } from "@/views/home/components/Expansion/shared/dateRange";
import { isRangeApplied } from "@/views/home/components/Expansion/shared/rangeFilter";

export const COMPANY_LIST_PER_PAGE = 25;

// Empty object when no window keeps "all time" behavior — the picker stays non-destructive even before backend support ships.
function dateRangeParams(range) {
  if (!range) return {};
  const out = {};
  // Canonical company-search date-range form: transitioned_dates [from, to].
  if (range.from && range.to) out.transitioned_dates = [range.from, range.to];
  if (range.window_days) out.window_days = range.window_days;
  return out;
}

// Job/Ads/News source filters use the same wire keys as company search (buildCompanyProfileFilters EXISTS clauses) so reused filter components serialize identically; omitted when empty.
function sourceFilterParams(rail) {
  const r = rail || {};
  const a = (v) => (Array.isArray(v) && v.length ? v : undefined);
  return {
    job_titles: a(r.job_titles), job_locations: a(r.job_locations),
    job_exclude_locations: a(r.job_exclude_locations), job_posted_dates: a(r.job_posted_dates),
    advertisement_target_locations: a(r.advertisement_target_locations),
    advertisement_exclude_target_locations: a(r.advertisement_exclude_target_locations),
    advertisement_search_terms: a(r.advertisement_search_terms),
    advertisement_start_dates: a(r.advertisement_start_dates),
    advertisement_end_dates: a(r.advertisement_end_dates),
    news_categories: a(r.news_categories), news_published_dates: a(r.news_published_dates),
    keywords: a(r.keywords),
    // AND/OR operators for the source filters (backend reads body.filter_conditions).
    filter_conditions: a(r.filter_conditions),
  };
}

// Rail filters → API in company-search canonical names (employees/founded_dates/revenues); origins sent per-builder as locations.
function railEmployeesParam(rail) {
  const r = rail || {};
  const ranges = [];
  for (const s of (Array.isArray(r.employee_slugs) ? r.employee_slugs : [])) {
    if (typeof s !== "string") continue;
    const [a, b] = s.split("-");
    const lo = Number(a);
    if (!Number.isFinite(lo)) continue;
    if (b == null || b === "") { ranges.push([lo]); continue; }
    const hi = Number(b);
    if (Number.isFinite(hi)) ranges.push([lo, hi]);
  }
  // Only a complete custom range filters. Saved views and natural-language results write these
  // fields straight into state, so the rule has to hold here and not only in the rail that typed it.
  if (isRangeApplied(r.employee_min, r.employee_max)) {
    ranges.push([Number(r.employee_min), Number(r.employee_max)]);
  }
  return ranges.length ? ranges : undefined;
}

function rangePairParam(min, max) {
  if (!isRangeApplied(min, max)) return undefined;
  return [Number(min), Number(max)];
}

function railFilterParams(rail) {
  const r = rail || {};
  const a = (v) => (Array.isArray(v) && v.length ? v : undefined);
  return {
    freshness: a(r.freshness), scopes: a(r.scopes),
    ahead_of_pace: r.ahead_of_pace === true ? true : undefined,
    vertical_categories: a(r.vertical_categories), vertical_sub_categories: a(r.vertical_sub_categories),
    verticals: a(r.verticals), technologies: a(r.technologies), industries: a(r.industries),
    exclude_country_codes: a(r.exclude_country_codes), exclude_verticals: a(r.exclude_verticals),
    exclude_locations: a(r.exclude_origins),
    companies: a(r.companies), company_groups: a(r.company_groups),
    employees: railEmployeesParam(r),
    founded_dates: rangePairParam(r.founded_min, r.founded_max),
    revenues: rangePairParam(r.revenue_min, r.revenue_max),
    only_contraction: r.only_contraction === true ? true : undefined,
    min_markets: Number(r.min_markets) >= 1 ? Number(r.min_markets) : undefined,
    min_signal_count: Number(r.min_signal_count) >= 1 ? Number(r.min_signal_count) : undefined,
    momentum: a(r.momentum),
    signal_strengths: a(r.signal_strengths), signal_types: a(r.signal_type_slugs),
    // verticals are free text; the backend only resolves them to ids via similarity when this flag is set (filter_resolve.js), else they match nothing.
    is_enable_similarity_search: (a(r.verticals) || a(r.exclude_verticals)) ? true : undefined,
  };
}

// Shared scope body for /dashboard, /lookup and /select — one spread order (geo → stages → rail → source → date) so a new filter key can't reach one surface and miss another.
function buildScopedRequestBody(state, profile_id, { home_country_code, stages } = {}) {
  const rail = state.rail_filters || {};
  const stage_list = stages !== undefined ? stages : state.selected_stage_slugs;
  return {
    profile_id,
    ...buildExpansionGeoPayload(state, { home_country_code }),
    stages: stage_list && stage_list.length ? stage_list : undefined,
    ...railFilterParams(rail),
    ...sourceFilterParams(rail),
    ...dateRangeParams(state.date_range),
  };
}

// From = rail.origins → froms, To = selected_country_list → tos; empty leg = "anywhere"; no mode-based reinterpretation. Either-direction and corridor compare keep the legacy country_codes shape.
function buildExpansionGeoPayload(state, { home_country_code } = {}) {
  const arr = (v) => (Array.isArray(v) && v.length ? v.slice() : []);
  const uniq = (a) => [...new Set(a)];
  // home_country_code arg wins over rail.origins.
  const froms = home_country_code !== undefined ? (home_country_code ? [home_country_code] : []) : arr((state.rail_filters || {}).origins);
  const tos = arr(state.selected_country_list);
  const either = state.route_direction === "either";

  // Map corridor compare (click two countries) keeps its own shape and home/destination semantics — fetchCountryDetail reads the same two keys.
  const outgoing = state.outgoing_target || null;
  const selected = state.selected_country || null;
  if (outgoing && selected) {
    if (either) return { country_codes: [selected, outgoing], corridor: true, direction: "all" };
    // Directed corridor honors the click's orientation: "Coming from" rows are inbound (outgoing → selected); flow-line / outgoing rows are selected → outgoing.
    if (state.corridor_flow === "inbound") return { froms: uniq([...froms, outgoing]), tos: [selected] };
    return { froms: uniq([...froms, selected]), tos: [outgoing] };
  }

  // Single-slot fallback: focusMarket / legacy views set only the mirror (list null); an origin-mirror (selected ∈ froms) is the From leg, never a To — excluding it keeps the phantom-To ghost dead.
  const to_leg = tos.length ? tos : selected && !froms.includes(selected) ? [selected] : [];

  // Either only matters with exactly one leg set ("Germany, either way"); with both legs the arrow says the direction, with neither there's nothing to widen — fall through to plain froms/tos.
  const one_leg_only = (froms.length > 0) !== (to_leg.length > 0);
  if (either && one_leg_only) {
    return { country_codes: uniq([...froms, ...to_leg]), direction: "all" };
  }
  // Both keys always sent (maybe empty) so the backend resolves is_global; empty+empty = all markets.
  return { froms, tos: to_leg };
}

// One-way with no legs and no corridor is meaningless — decay to either so the next direction-agnostic pick isn't silently scoped. Call after leg/route mutations (flow_direction is a getter now).
function decayEmptyRoute(state) {
  const has_leg = (Array.isArray((state.rail_filters || {}).origins) && state.rail_filters.origins.length > 0) || (Array.isArray(state.selected_country_list) && state.selected_country_list.length > 0);
  if (!has_leg && !state.outgoing_target && state.route_direction === "one_way") state.route_direction = "either";
}

// Outbound focus rides the From leg: when an origins write drops it (and no real To backs it) the focus must leave too, else a phantom "To: X" chip re-serializes into ?countries=.
function dropOrphanedFocus(state, old_origins, new_origins) {
  const focus = state.selected_country;
  if (!focus) return;
  if (Array.isArray(state.selected_country_list) && state.selected_country_list.length) return;
  const had = Array.isArray(old_origins) && old_origins.includes(focus);
  const has = Array.isArray(new_origins) && new_origins.includes(focus);
  if (had && !has) state.selected_country = null;
}

function legacyRouteDirection(state) {
  // A directed map corridor carries its own orientation — report it so toolbar and monitor filters describe the corridor instead of "all".
  if (state.outgoing_target && state.selected_country && state.route_direction !== "either") {
    return state.corridor_flow === "inbound" ? "inbound" : "outbound";
  }
  const len = (v) => (Array.isArray(v) ? v.length : 0);
  const has_from = len((state.rail_filters || {}).origins) > 0;
  const has_to = len(state.selected_country_list) > 0;
  if (state.route_direction === "either" && (has_from !== has_to)) return "all";
  if (has_to && !has_from) return "inbound";
  if (has_from && !has_to) return "outbound";
  return "all";
}

// Pre-rename saved_searches rows carry legacy keys; map to /search's wire format on restore. company_name_query has no UUID equivalent — drop.
function migrateLegacyRailFilters(rail) {
  if (!rail || typeof rail !== "object") return rail;
  const out = { ...rail };
  const promote = (legacy_key, new_key) => {
    if (out[legacy_key] !== undefined && out[new_key] === undefined) {
      out[new_key] = out[legacy_key];
    }
    delete out[legacy_key];
  };
  promote("industry_category_ids",     "vertical_categories");
  promote("industry_sub_category_ids", "vertical_sub_categories");
  promote("vertical_ids",              "verticals");
  promote("technology_ids",            "technologies");
  if (out.company_name_query !== undefined) delete out.company_name_query;
  return out;
}

// Empty-shape factory. Wire-format names mirror /search.
function defaultRailFilters() {
  return {
    directions: [], freshness: [], scopes: [], ahead_of_pace: false, origins: [],
    vertical_categories: [], vertical_sub_categories: [],
    verticals: [], technologies: [], industries: [],
    exclude_country_codes: [], exclude_verticals: [], exclude_origins: [],
    companies: [],
    // Saved-list scope from the For-you panel; backend filters es.domain_search_id by Profile_Companies.group_id.
    company_groups: [],
    employee_slugs: [], employee_min: null, employee_max: null,
    founded_min: null, founded_max: null,
    revenue_min: null, revenue_max: null,
    min_markets: null,
    min_signal_count: null,
    only_contraction: false,
    momentum: [],
    signal_strengths: [],
    signal_type_slugs: [],
    // Job / Advertisement / News / Keyword source filters + their AND/OR operators.
    job_titles: [], job_locations: [], job_exclude_locations: [], job_posted_dates: [],
    advertisement_target_locations: [], advertisement_exclude_target_locations: [],
    advertisement_search_terms: [], advertisement_start_dates: [], advertisement_end_dates: [],
    news_categories: [], news_published_dates: [], keywords: [],
    filter_conditions: [],
  };
}

// Canonical rail-filter key sets, derived once from the default shape — single source of truth for FindBar's checks + the URL blob (no parallel lists to drift).
export const RAIL_FILTER_ARRAY_KEYS = (() => {
  const d = defaultRailFilters();
  return Object.keys(d).filter((k) => Array.isArray(d[k]));
})();
export const RAIL_FILTER_SCALAR_KEYS = (() => {
  const d = defaultRailFilters();
  return Object.keys(d).filter((k) => typeof d[k] !== "boolean" && !Array.isArray(d[k]));
})();
export const RAIL_FILTER_BOOL_KEYS = (() => {
  const d = defaultRailFilters();
  return Object.keys(d).filter((k) => typeof d[k] === "boolean");
})();
// Not shareable filter VALUES (saved-list refs / operator meta) — kept out of the URL.
export const RAIL_URL_EXCLUDE_KEYS = ["company_groups", "filter_conditions"];
// Does the rail carry any narrowing filter? One check for the whole shape.
export function railHasActiveFilter(rail) {
  const r = rail || {};
  if (RAIL_FILTER_ARRAY_KEYS.some((k) => Array.isArray(r[k]) && r[k].length)) return true;
  if (RAIL_FILTER_BOOL_KEYS.some((k) => r[k] === true)) return true;
  if (RAIL_FILTER_SCALAR_KEYS.some((k) => r[k] != null && r[k] !== "" && Number.isFinite(Number(r[k])))) return true;
  return false;
}

// Shared by railSnapshot so a source-only change bumps the stale-fetch guard (else a slow source-filtered fetch could overwrite a newer one).
const RAIL_SOURCE_KEYS = [
  "job_titles", "job_locations", "job_exclude_locations", "job_posted_dates",
  "advertisement_target_locations", "advertisement_exclude_target_locations",
  "advertisement_search_terms", "advertisement_start_dates", "advertisement_end_dates",
  "news_categories", "news_published_dates", "keywords",
];

// Race-guard snapshot. Every defaultRailFilters() field MUST appear here.
function railSnapshot(rail) {
  if (!rail) return "";
  const arr = (xs) => (Array.isArray(xs) ? [...xs].sort().join(",") : "");
  const n = (v) => (v == null ? "" : String(v));
  // Date sub-fields are positional [start,end] — join unsorted to preserve order.
  const ord = (xs) => (Array.isArray(xs) ? xs.join(",") : "");
  const fc = (Array.isArray(rail.filter_conditions) ? rail.filter_conditions : [])
    .map((c) => (c ? `${c.key}:${c.operator}` : "")).sort().join(",");
  return [
    arr(rail.directions),
    arr(rail.freshness),
    arr(rail.scopes),
    rail.ahead_of_pace === true ? "1" : "0",
    arr(rail.origins),
    arr(rail.vertical_categories),
    arr(rail.vertical_sub_categories),
    arr(rail.verticals),
    arr(rail.exclude_country_codes),
    arr(rail.exclude_origins),
    arr(rail.exclude_verticals),
    arr(rail.technologies),
    arr(rail.companies),
    arr(rail.company_groups),
    arr(rail.employee_slugs),
    n(rail.employee_min),
    n(rail.employee_max),
    n(rail.founded_min),
    n(rail.founded_max),
    n(rail.revenue_min),
    n(rail.revenue_max),
    n(rail.min_markets),
    n(rail.min_signal_count),
    rail.only_contraction === true ? "1" : "0",
    arr(rail.signal_strengths),
    arr(rail.momentum),
    arr(rail.signal_type_slugs),
    ...RAIL_SOURCE_KEYS.map((k) => (k.endsWith("_dates") ? ord(rail[k]) : arr(rail[k]))),
    fc,
  ].join("|");
}

// In-flight promise cache: fetchSavedViews + createSavedView both resolve the type id — without it a quick "+ Save" after mount fires two concurrent GETs to /saved_search_types.
let _saved_search_type_id_promise = null;

// Largest flows_size already fetched for the current filter set — slider extends within it are no-ops (map re-slices client-side); resets to the base buffer on any refetch without density_n.
let _map_flows_loaded_size = 0;

const state = {
  dashboard: null,
  // Snapshot of dashboard.data from the most recent UNSCOPED fetch (no country_codes filter). Drives the chip row so it stays rich when multi-select narrows `dashboard` itself.
  dashboard_market_index: null,
  country_detail: null,
  // Outbound corridor destination (e.g. 'GB' when scoping China → UK); selected_country stays the HOME market; null = no corridor.
  outgoing_target: null,
  // Directed-corridor orientation ('inbound'|'outbound'|null) — set by the corridor click's SET_FLOW_DIRECTION word, read only while outgoing_target is set.
  corridor_flow: null,
  signal_types_catalog: {},
  // Export field picker options — cached once per session like the search export's companyExportFields.
  export_fields: [],
  // "Just moved" panel rows for global view — backend enriches with company_name + logo_url + home_country_code so each row renders without N+1 lookups.
  recent_movers: [],
  recent_movers_loading: false,

  // What's-new "Fast movers" tab: ahead_of_pace=true companies (depth signal).
  fast_movers: [],
  fast_movers_loading: false,

  // What's-new "Wide expanders" tab: ≥3 distinct markets in window (breadth signal) — different commercial intent from Fast movers (multi-market push, not single-corridor speed).
  wide_expanders: [],
  wide_expanders_loading: false,

  // For-you tab: caller's saved company lists, filtered to those whose members had stage transitions in the current window.
  saved_list_activity: [],
  saved_list_activity_loading: false,

  selected_country: null,
  // null = not in multi mode; CCA2 array = multi-market scope. Mutually exclusive with selected_country — each setter clears the other.
  selected_country_list: null,
  selected_stage_slugs: [],
  // Derived mirror = first selected stage; lets single-stage surfaces (map highlight, list header, KPI tiles) keep reading one slug while the rail filters on the full array.
  selected_stage_slug: null,
  map_mode: MAP_MODE.GLOBAL,
  // Route direction — source of truth. 'one_way' = From → To; 'either' = inbound ∪ outbound union for a single market (the one shape froms/tos cannot express).
  route_direction: "either",

  // Server-side rail filters. Shape lives in defaultRailFilters().
  rail_filters: defaultRailFilters(),

  // Natural-language Find bar: true while a typed query is being parsed + applied.
  nl_loading: false,

  client_added_country_codes: [],

  // Saved Views reuse /search's saved-search backend: search_type_id resolved at runtime (slug='expansion'); active_view_id null = "All / unsaved"; parameters = full restore payload.
  saved_views: {
    search_type_id: null,
    views: [],
    active_view_id: null,
    loading: false,
    // True after the first fetch (success OR failure) so a broken env doesn't retry on every remount; reset by RESET, bypassed by { force: true }.
    fetch_attempted: false,
  },

  // Date window for all aggregates; default matches new_transitions_30d. Null window = "all time".
  date_range: { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS },

  company_page: 1,
  company_per_page: COMPANY_LIST_PER_PAGE,
  // List order: "relevance" (default, evidence-ranked) or "recent" (newest-first). Only sent when "recent".
  list_sort: "relevance",

  loading: {
    dashboard: false,
    country_detail: false,
    signal_types: false,
  },

  // loading.* booleans derive as counter > 0 so overlapping fetches (rapid filter switches) don't toggle loading off while a second fetch is still in flight.
  loading_counter: {
    dashboard: 0,
    country_detail: 0,
  },

  error: {
    dashboard: null,
    country_detail: null,
  },
};

const getters = {
  // Legacy 'all'|'inbound'|'outbound' mirror of the route — DERIVED, so it can never go stale (was a hand-synced state key that three separate bugs forgot to sync).
  flow_direction(state) {
    return legacyRouteDirection(state);
  },
  visibleCountryChips(state) {
    if (!state.dashboard) return [];
    // User picks (single + multi) bypass the MAX_VISIBLE_MARKETS cap so the chip row never hides the user's selection behind "+N more"; the cap still limits filler chips (watchlist + added + top).
    const selected = state.selected_country ? [state.selected_country] : [];
    const multi    = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const watchlist = (state.dashboard.watchlist_markets || []).map((row) => row.country_code);
    const added = state.client_added_country_codes;
    const market_source = state.dashboard_market_index || state.dashboard;
    const top = (market_source?.data || []).map((row) => row.country_code);

    const seen = new Set();
    const out = [];
    // Pass 1: always-keep — every user pick, no cap.
    [...selected, ...multi].forEach((code) => {
      if (!code || seen.has(code)) return;
      seen.add(code);
      out.push(code);
    });
    // Pass 2: filler chips up to MAX_VISIBLE_MARKETS total.
    [...watchlist, ...added, ...top].forEach((code) => {
      if (!code || seen.has(code)) return;
      if (out.length >= MAX_VISIBLE_MARKETS) return;
      seen.add(code);
      out.push(code);
    });
    return out;
  },

  totalAvailableMarkets(state) {
    // Read from the unscoped index so "+N more" reflects all markets, not just the multi-selected subset.
    const source = state.dashboard_market_index || state.dashboard;
    if (!source) return 0;
    return (source.data || []).length;
  },

  defaultCountryCode(state, getters) {
    if (state.selected_country) return state.selected_country;
    const list = getters.visibleCountryChips;
    return list[0] || null;
  },

  topCountryByActivity(state) {
    if (!state.dashboard || !state.dashboard.data || !state.dashboard.data.length) return null;
    return state.dashboard.data[0];
  },

  // SELECTED (country picked): all 4 tiles describe it. GLOBAL: tile 1 = movement metric; tiles 2-4 use hybrid score (count × √rate) and skip top-3 by volume so next-tier markets surface.
  kpiSummary(state) {
    const data = (state.dashboard && state.dashboard.data) || [];
    const selected_row = state.selected_country
      ? data.find((r) => r.country_code === state.selected_country)
      : null;

    // Row missing from `data` (window filtered / dropped from top-N): stay in SELECTED mode with null metric rows so the UI says "no signals yet in this window" instead of flipping back to GLOBAL.
    if (state.selected_country) {
      if (!selected_row) {
        const stub = { country_code: state.selected_country, total: 0, count: 0, rate: null, active_markets: data.length };
        return {
          is_user_selected: true,
          no_data_in_window: true,
          tile1_focus: stub,
          tile1_global: null,
          best_to_contact: null,
          moving_fast: null,
          new_activity: null,
        };
      }
      const total = Number(selected_row.total_companies_with_signals) || 1;
      const tile = (count) => kpiRow(selected_row, data.length, count, count / total);
      return {
        is_user_selected: true,
        no_data_in_window: false,
        tile1_focus: kpiRow(selected_row, data.length),
        tile1_global: null,
        best_to_contact: tile(Number(selected_row.expanding_count) || 0),
        moving_fast: tile(Number(selected_row.pairs_ahead_of_pace_30d) || 0),
        new_activity: tile(Number(selected_row.new_transitions_30d) || 0),
      };
    }

    // One pass: top-3 by volume (excluded), best-by-score per metric, global aggregates. count × √rate lets Singapore (200×√0.25=100) beat Maldives (18×√0.56=13.5) without a volume floor.
    const top_volume = []; // ascending-sorted, kept at length ≤ 3
    let total_new = 0;
    let active = 0;
    const scored = data.map((r) => {
      const total = Number(r.total_companies_with_signals) || 1;
      const fresh_n = Number(r.new_transitions_30d) || 0;
      total_new += fresh_n;
      if (fresh_n > 0) active += 1;
      insertTop(top_volume, r, total, 3);
      return { row: r, total, fresh_n };
    });
    const exclude_set = new Set(top_volume.map((r) => r.country_code));

    const bestByScore = (key) => {
      let best = null;
      for (const { row, total } of scored) {
        if (exclude_set.has(row.country_code)) continue;
        const count = Number(row[key]) || 0;
        if (count <= 0) continue;
        const rate = count / total;
        const score = count * Math.sqrt(rate);
        if (!best || score > best.score) best = { row, count, rate, score };
      }
      return best;
    };

    const expanding = bestByScore("expanding_count");
    const pace = bestByScore("pairs_ahead_of_pace_30d");
    const fresh = bestByScore("new_transitions_30d");

    return {
      is_user_selected: false,
      tile1_focus: null,
      tile1_global: { new_transitions: total_new, active_markets: active, total_markets: data.length },
      best_to_contact: expanding && kpiRow(expanding.row, data.length, expanding.count, expanding.rate),
      moving_fast: pace && kpiRow(pace.row, data.length, pace.count, pace.rate),
      new_activity: fresh && kpiRow(fresh.row, data.length, fresh.count, fresh.rate),
    };
  },
};

// Length-N min-heap-ish list of highest-`total` rows; O(N) insert with N=3 beats sorting all rows for top-3.
function insertTop(list, row, total, cap) {
  if (list.length < cap) {
    list.push(row);
    list.sort((a, b) => (Number(a.total_companies_with_signals) || 0) - (Number(b.total_companies_with_signals) || 0));
    return;
  }
  const smallest = Number(list[0].total_companies_with_signals) || 0;
  if (total <= smallest) return;
  list[0] = row;
  list.sort((a, b) => (Number(a.total_companies_with_signals) || 0) - (Number(b.total_companies_with_signals) || 0));
}

// Tile 1 only needs country + total; tiles 2-4 need the metric value + rate.
function kpiRow(row, active_markets, count = 0, rate = null) {
  if (!row) return null;
  return {
    country_code: row.country_code,
    total: Number(row.total_companies_with_signals) || 0,
    count: Number(count) || 0,
    rate,
    active_markets,
  };
}

// Flattens {countries, top_flows,...} so dashboard.data stays the countries array (legacy array passes through); dashboard_market_index must flatten identically or `.data.map` blows up on map + chips.
function normalizeDashboardPayload(payload) {
  if (payload && !Array.isArray(payload) && payload.data && !Array.isArray(payload.data)) {
    const c = payload.data;
    return { ...payload, ...c, data: Array.isArray(c.countries) ? c.countries : [] };
  }
  return payload;
}

const mutations = {
  SET_DASHBOARD(state, payload) { state.dashboard = normalizeDashboardPayload(payload); },
  SET_NL_LOADING(state, payload) { state.nl_loading = payload === true; },
  SET_DASHBOARD_MARKET_INDEX(state, payload) { state.dashboard_market_index = normalizeDashboardPayload(payload); },
  // Translates legacy 'all'|'inbound'|'outbound' (toolbar/?dir/corridors) into the route; moves a leg only when the destination leg is empty so an existing From→To is never lost.
  SET_FLOW_DIRECTION(state, payload) {
    const rail = state.rail_filters || {};
    const arr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
    if (payload !== "inbound" && payload !== "outbound") {
      state.route_direction = "either";
      state.corridor_flow = null;
      return;
    }
    state.route_direction = "one_way";
    // Remember the click's word — a directed map corridor (outgoing_target set before/after this commit) reads it to orient froms/tos.
    state.corridor_flow = payload;
    const froms = arr(rail.origins);
    // Single-slot fallback: focusMarket keeps the market in selected_country only (list null) — without it the toolbar flip silently no-ops in that state.
    const list = arr(state.selected_country_list);
    const tos = list.length ? list : state.selected_country && !froms.includes(state.selected_country) ? [state.selected_country] : [];
    if (payload === "inbound" && !tos.length && froms.length) {
      state.selected_country_list = froms;
      state.selected_country = froms.length === 1 ? froms[0] : null;
      state.rail_filters = { ...rail, origins: [] };
    } else if (payload === "outbound" && !froms.length && tos.length) {
      state.rail_filters = { ...rail, origins: tos };
      state.selected_country_list = null;
      // Keep the focus on the origin (mirrors the inbound branch) so the market card survives the flip — clearing it makes the card vanish in outbound.
      state.selected_country = tos.length === 1 ? tos[0] : null;
    }
    // No decay here: ?dir commits before the f-blob delivers origins; decaying
    // on the transiently-empty legs reverted directed links to the either-union.
    // Leg-REMOVING mutations still decay, and this one only moves legs.
  },
  SET_ROUTE_DIRECTION(state, payload) {
    state.route_direction = payload === "either" ? "either" : "one_way";
    // No decay here: the rail declares one_way BEFORE emit() delivers its legs;
    // decaying in that window reverted every rail edit to the either-union.
    // Leg-REMOVING mutations still decay, so one_way can't outlive its last leg.
  },
  // Route legs: From = rail_filters.origins (travels with the rail payload); To = selected_country_list.
  SET_ROUTE_FROM(state, payload) {
    // Uppercased like SET_SELECTED_COUNTRY_LIST so leg/focus comparisons and the self-loop check never miss on case (old links carried lowercase).
    const clean = Array.isArray(payload) ? payload.filter(Boolean).map((c) => String(c).toUpperCase()) : [];
    const old_origins = (state.rail_filters || {}).origins;
    state.rail_filters = { ...(state.rail_filters || {}), origins: clean };
    dropOrphanedFocus(state, old_origins, clean);
    decayEmptyRoute(state);
  },
  SET_COUNTRY_DETAIL(state, payload) {
    // Normalize to what components read (.companies / .data=aggregate / .metadata.pagination); tolerant of extras under `metadata` (canonical), under `data` (interim), or a flat array.
    if (payload && !Array.isArray(payload)) {
      const container = payload.data && !Array.isArray(payload.data) ? payload.data : {};
      const meta = payload.metadata && !Array.isArray(payload.metadata) ? payload.metadata : {};
      const list = Array.isArray(payload.companies) ? payload.companies
                 : Array.isArray(container.companies) ? container.companies
                 : Array.isArray(payload.data) ? payload.data
                 : [];
      const aggregate = payload.aggregate !== undefined ? payload.aggregate
                      : container.aggregate !== undefined ? container.aggregate
                      : meta.aggregate;
      const pagination = payload.pagination || container.pagination || meta.pagination;
      const metadata = { ...meta, ...container.metadata, pagination };
      state.country_detail = { ...meta, ...payload, ...container, companies: list, data: aggregate, metadata };
    } else {
      state.country_detail = payload;
    }
  },
  SET_OUTGOING_TARGET(state, payload) {
    state.outgoing_target = payload || null;
    // Orientation belongs to the corridor — clearing the target retires it.
    if (!state.outgoing_target) state.corridor_flow = null;
    // Clearing a focus-only map corridor leaves no legs — decay one_way so the next pick isn't silently direction-scoped.
    decayEmptyRoute(state);
  },
  SET_SIGNAL_TYPES(state, payload) { state.signal_types_catalog = payload || {}; },
  SET_EXPORT_FIELDS(state, payload) { state.export_fields = Array.isArray(payload) ? payload : []; },
  SET_RECENT_MOVERS(state, payload) { state.recent_movers = Array.isArray(payload) ? payload : []; },
  SET_RECENT_MOVERS_LOADING(state, payload) { state.recent_movers_loading = !!payload; },
  SET_FAST_MOVERS(state, payload) { state.fast_movers = Array.isArray(payload) ? payload : []; },
  SET_FAST_MOVERS_LOADING(state, payload) { state.fast_movers_loading = !!payload; },
  SET_WIDE_EXPANDERS(state, payload) { state.wide_expanders = Array.isArray(payload) ? payload : []; },
  SET_WIDE_EXPANDERS_LOADING(state, payload) { state.wide_expanders_loading = !!payload; },
  SET_SAVED_LIST_ACTIVITY(state, payload) { state.saved_list_activity = Array.isArray(payload) ? payload : []; },
  SET_SAVED_LIST_ACTIVITY_LOADING(state, payload) { state.saved_list_activity_loading = !!payload; },

  SET_SELECTED_COUNTRY(state, code) {
    state.selected_country = code;
    // Single-country mode wins — clear any multi selection so they don't fight.
    if (code != null) state.selected_country_list = null;
    decayEmptyRoute(state);
  },
  SET_SELECTED_COUNTRY_LIST(state, codes) {
    // null / [] / non-array → clear multi mode
    const cleaned = Array.isArray(codes)
      ? [...new Set(codes.filter(Boolean).map((c) => String(c).toUpperCase()))]
      : null;
    state.selected_country_list = (cleaned && cleaned.length > 0) ? cleaned : null;
    // Legacy mirror — selected_country_list is the single source of truth; selected_country is a derived 1-item alias.
    state.selected_country = (cleaned && cleaned.length === 1) ? cleaned[0] : null;
    decayEmptyRoute(state);
  },
  // Accepts an array (rail multi-select), a single slug (map/KPI drill), or null/empty (clear) → normalized to an array; keeps the single mirror in sync.
  SET_SELECTED_STAGE(state, slugs) {
    state.selected_stage_slugs = Array.isArray(slugs) ? slugs.filter(Boolean) : slugs ? [slugs] : [];
    state.selected_stage_slug = state.selected_stage_slugs[0] || null;
  },
  SET_MAP_MODE(state, mode) { state.map_mode = mode; },
  SET_RAIL_FILTERS(state, filters) {
    const arr = (v) => (Array.isArray(v) ? v : []);
    const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
    const old_origins = (state.rail_filters || {}).origins;
    state.rail_filters = {
      directions: arr(filters?.directions),
      freshness:  arr(filters?.freshness),
      scopes:     arr(filters?.scopes),
      ahead_of_pace: filters?.ahead_of_pace === true,
      // Uppercased like SET_SELECTED_COUNTRY_LIST (see SET_ROUTE_FROM).
      origins:    arr(filters?.origins).filter(Boolean).map((c) => String(c).toUpperCase()),
      vertical_categories:     arr(filters?.vertical_categories),
      vertical_sub_categories: arr(filters?.vertical_sub_categories),
      verticals:    arr(filters?.verticals),
      exclude_country_codes: arr(filters?.exclude_country_codes),
      exclude_origins: arr(filters?.exclude_origins),
      exclude_verticals: arr(filters?.exclude_verticals),
      technologies: arr(filters?.technologies),
      industries:   arr(filters?.industries),
      companies:    arr(filters?.companies),
      company_groups: arr(filters?.company_groups),
      employee_slugs: arr(filters?.employee_slugs),
      employee_min:   num(filters?.employee_min),
      employee_max:   num(filters?.employee_max),
      founded_min:    num(filters?.founded_min),
      founded_max:    num(filters?.founded_max),
      revenue_min:    num(filters?.revenue_min),
      revenue_max:    num(filters?.revenue_max),
      min_markets:    num(filters?.min_markets),
      min_signal_count: num(filters?.min_signal_count),
      only_contraction: filters?.only_contraction === true,
      momentum: arr(filters?.momentum),
      signal_strengths: arr(filters?.signal_strengths),
      signal_type_slugs: arr(filters?.signal_type_slugs),
      // Source filters (Jobs/Ads/News) — arr() keeps order; date sub-fields are positional [start,end].
      job_titles:                             arr(filters?.job_titles),
      job_locations:                          arr(filters?.job_locations),
      job_exclude_locations:                  arr(filters?.job_exclude_locations),
      job_posted_dates:                       arr(filters?.job_posted_dates),
      advertisement_target_locations:         arr(filters?.advertisement_target_locations),
      advertisement_exclude_target_locations: arr(filters?.advertisement_exclude_target_locations),
      advertisement_search_terms:             arr(filters?.advertisement_search_terms),
      advertisement_start_dates:              arr(filters?.advertisement_start_dates),
      advertisement_end_dates:                arr(filters?.advertisement_end_dates),
      news_categories:                        arr(filters?.news_categories),
      news_published_dates:                   arr(filters?.news_published_dates),
      keywords:                               arr(filters?.keywords),
      // AND/OR operators [{key, operator}] for the source filters.
      filter_conditions:                      arr(filters?.filter_conditions),
    };
    // origins is the route's From leg, so the mirror moves with it.
    dropOrphanedFocus(state, old_origins, state.rail_filters.origins);
    decayEmptyRoute(state);
  },
  SET_DATE_RANGE(state, { from, to, window_days }) {
    state.date_range = {
      from: from || null,
      to: to || null,
      window_days: Number(window_days) || null,
    };
  },

  ADD_CLIENT_COUNTRY(state, code) {
    if (!code) return;
    if (state.client_added_country_codes.includes(code)) return;
    state.client_added_country_codes = [...state.client_added_country_codes, code];
  },
  REMOVE_CLIENT_COUNTRY(state, code) {
    state.client_added_country_codes = state.client_added_country_codes.filter((c) => c !== code);
  },

  SET_COMPANY_PAGE(state, page) { state.company_page = page; },
  SET_LIST_SORT(state, v) { state.list_sort = v === "recent" ? "recent" : "relevance"; },
  SET_COMPANY_PER_PAGE(state, n) { state.company_per_page = n; },

  // In-place key writes — loading/error are initialized with all keys so Vue tracks them; avoids fresh object allocation + spurious re-renders on every fetch tick.
  SET_LOADING(state, { key, value }) { state.loading[key] = value; },
  INC_LOADING(state, key) {
    if (state.loading_counter[key] == null) state.loading_counter[key] = 0;
    state.loading_counter[key]++;
    state.loading[key] = state.loading_counter[key] > 0;
  },
  DEC_LOADING(state, key) {
    if (state.loading_counter[key] == null) state.loading_counter[key] = 0;
    state.loading_counter[key] = Math.max(0, state.loading_counter[key] - 1);
    state.loading[key] = state.loading_counter[key] > 0;
  },
  SET_ERROR(state, { key, value }) { state.error[key] = value; },

  // Saved views.
  SET_SAVED_VIEWS_TYPE_ID(state, id) { state.saved_views.search_type_id = id; },
  SET_SAVED_VIEWS(state, list) { state.saved_views.views = Array.isArray(list) ? list : []; },
  SET_ACTIVE_VIEW_ID(state, id) { state.saved_views.active_view_id = id || null; },
  SET_SAVED_VIEWS_LOADING(state, v) { state.saved_views.loading = v === true; },
  SET_SAVED_VIEWS_FETCH_ATTEMPTED(state, v) { state.saved_views.fetch_attempted = v === true; },
  UPSERT_SAVED_VIEW(state, view) {
    if (!view || view.saved_search_id == null) return;
    const i = state.saved_views.views.findIndex((v) => v.saved_search_id === view.saved_search_id);
    if (i >= 0) {
      state.saved_views.views = state.saved_views.views.map((v, idx) => (idx === i ? { ...v, ...view } : v));
    } else {
      state.saved_views.views = [view, ...state.saved_views.views];
    }
  },
  REMOVE_SAVED_VIEW(state, id) {
    state.saved_views.views = state.saved_views.views.filter((v) => v.saved_search_id !== id);
    if (state.saved_views.active_view_id === id) state.saved_views.active_view_id = null;
  },

  RESET(state) {
    state.dashboard = null;
    state.dashboard_market_index = null;
    state.country_detail = null;
    state.selected_country = null;
    state.selected_country_list = null;
    state.selected_stage_slugs = [];
    state.selected_stage_slug = null;
    state.route_direction = "either";
    // RESET runs on logout AND account switch — must clear the corridor or it keeps scoping /lookup for the next account.
    state.outgoing_target = null;
    state.corridor_flow = null;
    state.client_added_country_codes = [];
    state.company_page = 1;
    state.rail_filters = defaultRailFilters();
    state.saved_views = { search_type_id: state.saved_views?.search_type_id || null, views: [], active_view_id: null, loading: false, fetch_attempted: false };
  },
};

const actions = {
  async fetchDashboard({ commit, state, rootState }, { force = false, density_n } = {}) {
    // High-water-mark: a slider extend within already-fetched arcs is a no-op (map re-slices client-side); returning before INC/DEC also avoids loading flicker.
    if (density_n && !force && Math.max(MAP_FLOWS_REQUEST_SIZE, flowsSizeForDensity(density_n)) <= _map_flows_loaded_size) {
      return state.dashboard;
    }
    commit("INC_LOADING", "dashboard");
    commit("SET_ERROR", { key: "dashboard", value: null });
    try {
      const profile_id = rootState.user?.currentId?.profile_id;
      // Don't fire the POST before auth hydrates — a missing profile_id 400s (mirrors company-search's pre-request guard).
      if (!profile_id) { commit("DEC_LOADING", "dashboard"); return null; }
      // Map scope = the exact shared body the list + select-all send, so the map can't drift from them (unfiltered 266 vs filtered 258 class).
      const is_corridor = Boolean((state.outgoing_target || null) && state.selected_country);
      const buildBody = (flows_size) => ({
        ...buildScopedRequestBody(state, profile_id),
        flows_size,
      });
      // Backend silently clamps flows_size to the plan cap (never 416) — request what the density needs and let the server truncate; density_n present means the slider crossed DEFAULT, so upsize.
      const initial_flows_size = density_n
        ? Math.max(MAP_FLOWS_REQUEST_SIZE, flowsSizeForDensity(density_n))
        : MAP_FLOWS_REQUEST_SIZE;
      const res = await getExpansionDashboard(buildBody(initial_flows_size));
      // density_n present = slider extend (raise the mark); absent = filter/base reload at the buffer, so reset the mark to the base buffer size.
      _map_flows_loaded_size = density_n ? initial_flows_size : MAP_FLOWS_REQUEST_SIZE;
      commit("SET_DASHBOARD", res);
      // Refresh the unscoped market index only on truly-global fetches — a From-only route IS scoped: writing its narrowed response here silently narrows the chip row to what that origin reaches.
      const scoped_tos = Array.isArray(state.selected_country_list) && state.selected_country_list.length > 0;
      const scoped_froms = Array.isArray((state.rail_filters || {}).origins) && state.rail_filters.origins.length > 0;
      const isMultiFetch = scoped_tos || scoped_froms;
      if (!isMultiFetch && !is_corridor) {
        commit("SET_DASHBOARD_MARKET_INDEX", res);
      } else if (!state.dashboard_market_index) {
        // Deep-link / saved-view restores land straight in multi mode — seed the null market_index with one background unscoped fetch or the chip row degrades to just the picks. Fire-and-forget.
        getExpansionDashboard({ profile_id, flows_size: TOP_FLOWS_RETRY_SIZE, ...dateRangeParams(state.date_range) })
          .then((unscoped) => { if (unscoped) commit("SET_DASHBOARD_MARKET_INDEX", unscoped); })
          .catch(() => { /* non-critical — chip row degrades gracefully */ });
      }
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return null;
      commit("SET_ERROR", { key: "dashboard", value: err?.message || "load_failed" });
      throw err;
    } finally {
      commit("DEC_LOADING", "dashboard");
    }
  },

  async fetchCountryDetail({ commit, state, rootState }, { country_code, page, per_page, stage_slugs, home_country_code } = {}) {
    const stageKey = (a) => (Array.isArray(a) ? a : a ? [a] : []).slice().sort().join(",");
    // Outbound: the focus IS the single From origin, but origins writers never touch selected_country — mirror it here so the card renders and the stale-guard keeps this response; >1 origin = no focus.
    if (country_code === undefined && legacyRouteDirection(state) === "outbound") {
      const origins = Array.isArray(state.rail_filters && state.rail_filters.origins) ? state.rail_filters.origins : [];
      const focus = origins.length === 1 ? origins[0] : null;
      if (state.selected_country !== focus) commit("SET_SELECTED_COUNTRY", focus);
    }
    // null country_code = global aggregate; threaded through the race guard so two consecutive global fetches don't stomp on each other.
    const code = country_code !== undefined ? country_code : state.selected_country;
    const requested_country = code || null;
    // Store-sourced so the corridor sticks across pagination/bulk; kept for the stale-response guard below.
    const requested_outgoing = state.outgoing_target || null;
    const requested_stages = stage_slugs === undefined ? state.selected_stage_slugs || [] : Array.isArray(stage_slugs) ? stage_slugs : stage_slugs ? [stage_slugs] : [];
    const requested_rail_snapshot = railSnapshot(state.rail_filters);
    const requested_sort = state.list_sort;
    // Route direction changes the wire shape (either-union vs froms/tos), so a pre-flip in-flight response must not land on a post-flip view.
    const requested_route = state.route_direction;
    commit("INC_LOADING", "country_detail");
    commit("SET_ERROR", { key: "country_detail", value: null });
    try {
      const profile_id = rootState.user?.currentId?.profile_id;
      const res = await getExpansionLookup({
        ...buildScopedRequestBody(state, profile_id, { home_country_code, stages: requested_stages }),
        page: page || state.company_page,
        per_page: per_page || state.company_per_page,
        sort_by: state.list_sort === "recent" ? "recent" : undefined,
      });
      // Drop stale responses: KPI tile clicks fire 3 fetches (selectCountry, selectStage, applyRailFilters) and only the last carries the right rail filter — a slow first fetch could overwrite it.
      if (
        requested_country !== (state.selected_country || null)
        || requested_outgoing !== (state.outgoing_target || null)
        || stageKey(requested_stages) !== stageKey(state.selected_stage_slugs)
        || requested_rail_snapshot !== railSnapshot(state.rail_filters)
        || requested_sort !== state.list_sort
        || requested_route !== state.route_direction
      ) return null;
      commit("SET_COUNTRY_DETAIL", res);
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return null;
      commit("SET_ERROR", { key: "country_detail", value: err?.message || "load_failed" });
      throw err;
    } finally {
      // Every INC pairs with a DEC unconditionally — gating DEC on staleness leaves the counter stuck >0 (map loading animation never clears).
      commit("DEC_LOADING", "country_detail");
    }
  },

  async fetchCompanyIds({ state, rootState }, { select_size, home_country_code } = {}) {
    // Mirrors fetchCountryDetail's payload (shared body incl. company_groups) so "Select all N" picks from the same pool the visible list shows.
    const profile_id = rootState.user?.currentId?.profile_id;
    const res = await getExpansionSelect({
      ...buildScopedRequestBody(state, profile_id, { home_country_code }),
      // sort_by mirrors the list so a capped "Select all N" keeps the same top rows the list shows.
      sort_by: state.list_sort === "recent" ? "recent" : undefined,
      select_size,
    });
    return res?.data || [];
  },

  async fetchSignalTypes({ commit, state }) {
    if (Object.keys(state.signal_types_catalog).length) return state.signal_types_catalog;
    commit("SET_LOADING", { key: "signal_types", value: true });
    try {
      const res = await getExpansionSignalTypes({});
      const data = res?.data || res || [];
      commit("SET_SIGNAL_TYPES", data);
      return data;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      return [];
    } finally {
      commit("SET_LOADING", { key: "signal_types", value: false });
    }
  },

  // Fast movers: ahead_of_pace=true companies. Scope follows the map's current selection.
  async fetchFastMovers({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) {
      commit("SET_FAST_MOVERS", []);
      return [];
    }
    const unused_rail = state.rail_filters || {};
    commit("SET_FAST_MOVERS_LOADING", true);
    try {
      // Shared body = the panel has NO hidden filters vs its "View full company list" drawer; ahead_of_pace after the spread overrides the rail toggle (fast movers are ahead-of-pace by definition).
      const res = await getExpansionLookup({
        ...buildScopedRequestBody(state, profile_id),
        page:     1,
        per_page: COMPANY_LIST_PER_PAGE,
        ahead_of_pace: true,
      });
      // /lookup shape varies by build — `companies` (canonical), `data` (legacy paginated wrapper), or a raw array; read whichever exists.
      const list = Array.isArray(res?.data?.companies) ? res.data.companies
                : Array.isArray(res?.companies) ? res.companies
                : Array.isArray(res?.data)      ? res.data
                : Array.isArray(res)            ? res
                : [];
      commit("SET_FAST_MOVERS", list);
      return list;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_FAST_MOVERS", []);
      return [];
    } finally {
      commit("SET_FAST_MOVERS_LOADING", false);
    }
  },

  // Wide expanders ARE the min_markets filter — previews the exact /lookup the "View full company list" footer lands on so panel and list match by construction (mirrors fetchFastMovers).
  async fetchWideExpanders({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) { commit("SET_WIDE_EXPANDERS", []); return []; }
    const rail = state.rail_filters || {};
    const multi = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list : undefined;
    const focus_is_origin = !!state.selected_country && Array.isArray(rail.origins) && rail.origins.includes(state.selected_country);
    commit("SET_WIDE_EXPANDERS_LOADING", true);
    try {
      const res = await getExpansionLookup({
        profile_id,
        country_codes: multi,
        // The outbound focus mirror (selected_country ∈ origins) is the FROM side — sent as the legacy TARGET scalar next to locations it makes target=home ∧ home≠target: matches nothing.
        country_code:  multi || focus_is_origin ? undefined : (state.selected_country || undefined),
        is_global:     multi ? undefined : (state.selected_country && !focus_is_origin ? undefined : true),
        direction:     legacyRouteDirection(state),
        page:          1,
        per_page:      COMPANY_LIST_PER_PAGE,
        locations:     (Array.isArray(rail.origins) && rail.origins.length) ? rail.origins : undefined,
        ...railFilterParams(rail),
        ...sourceFilterParams(rail),
        // Set after the spread so the panel is always the 3+ markets set, regardless of the rail toggle.
        min_markets:   WIDE_EXPANDERS_MIN_MARKETS,
        ...dateRangeParams(state.date_range),
      });
      const list = Array.isArray(res?.data?.companies) ? res.data.companies
                : Array.isArray(res?.companies) ? res.companies
                : Array.isArray(res?.data)      ? res.data
                : Array.isArray(res)            ? res
                : [];
      commit("SET_WIDE_EXPANDERS", list);
      return list;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_WIDE_EXPANDERS", []);
      return [];
    } finally {
      commit("SET_WIDE_EXPANDERS_LOADING", false);
    }
  },

  // For-you tab: same window/direction/country params as the other dashboard reads — backend returns only lists whose members moved in that window.
  async fetchSavedListActivity({ commit, state, rootState }) {
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) { commit("SET_SAVED_LIST_ACTIVITY", []); return []; }
    const multi = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list : undefined;
    commit("SET_SAVED_LIST_ACTIVITY_LOADING", true);
    try {
      const res = await getExpansionSavedListActivity({
        profile_id,
        country_codes: multi,
        country_code:  multi ? undefined : (state.selected_country || undefined),
        direction:     legacyRouteDirection(state),
        ...dateRangeParams(state.date_range),
      });
      const lists = Array.isArray(res?.lists) ? res.lists : [];
      commit("SET_SAVED_LIST_ACTIVITY", lists);
      return lists;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      commit("SET_SAVED_LIST_ACTIVITY", []);
      return [];
    } finally {
      commit("SET_SAVED_LIST_ACTIVITY_LOADING", false);
    }
  },

  // Cross-market recent transitions for the global "Just moved" panel; backend enriches with company name + logo + home_country_code.
  async fetchRecentMovers({ commit, rootState }, { days = 30, select_size, limit } = {}) {
    commit("SET_RECENT_MOVERS_LOADING", true);
    try {
      const since_at = new Date(Date.now() - days * 86400000).toISOString();
      const res = await getExpansionRecent({
        profile_id: rootState.user.currentId.profile_id,
        since_at,
        // `select_size` is the API's standard list-size param; legacy `limit` still accepted by the backend + here.
        select_size: select_size != null ? select_size : (limit != null ? limit : 25),
      });
      const transitions = res?.transitions || [];
      commit("SET_RECENT_MOVERS", transitions);
      return transitions;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") return [];
      return [];
    } finally {
      commit("SET_RECENT_MOVERS_LOADING", false);
    }
  },

  selectCountry({ commit, dispatch, state }, country_code) {
    // No cache-by-country check: it returns stale country_detail when flow_direction/stage/date/rail changed between deselect + reselect; the fetchCountryDetail race guard handles staleness.
    const current_list = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const is_same_single = current_list.length === 1 && current_list[0] === country_code;
    if (!is_same_single) {
      commit("SET_COUNTRY_DETAIL", null);
    }
    return dispatch("setSelectedCountries", country_code ? [country_code] : null);
  },

  // Switch country + stage + map mode in one shot; bypasses selectCountry's dedup so a same-country/different-stage click still refetches.
  focusMarket({ commit, dispatch, state }, { country_code, stage_slug = null } = {}) {
    if (!country_code) return Promise.resolve(null);
    const next_stage = stage_slug == null ? null : stage_slug;
    const cur_stages = state.selected_stage_slugs || [];
    const same_country = country_code === state.selected_country;
    const same_stage = next_stage == null ? cur_stages.length === 0 : cur_stages.length === 1 && cur_stages[0] === next_stage;
    if (same_country && same_stage && state.map_mode === MAP_MODE.COUNTRY && state.country_detail) {
      return Promise.resolve(state.country_detail);
    }
    if (!same_country) {
      commit("SET_COUNTRY_DETAIL", null);
      commit("SET_OUTGOING_TARGET", null);
    }
    commit("SET_SELECTED_STAGE", next_stage);
    commit("SET_SELECTED_COUNTRY", country_code);
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.COUNTRY);
    // Dashboard too — focusing one market out of a compared basket used to rescope the list
    // while the map kept the whole basket's arcs and counts.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code, stage_slugs: next_stage == null ? [] : [next_stage] }),
    ]);
  },

  // Flow-line click: focus HOME + scope DESTINATION in one atomic commit set, KEEPING outgoing_target so the list lands on the corridor; direction follows the arc so counts match the line's label.
  applyCorridor({ commit, dispatch, state }, { home, target, direction = "outbound" } = {}) {
    if (!home || !target) return Promise.resolve(null);
    if (home !== state.selected_country) commit("SET_COUNTRY_DETAIL", null);
    commit("SET_SELECTED_COUNTRY", home);
    commit("SET_OUTGOING_TARGET", target);
    commit("SET_FLOW_DIRECTION", direction);
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.COUNTRY);
    // Refresh BOTH surfaces so the map scopes to the corridor like the list — without fetchDashboard the map keeps the previous global flow (shows US->UK for a US->NL corridor).
    return Promise.all([
      dispatch("fetchDashboard", { force: true }).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: home }),
    ]);
  },

  // List order toggle (relevance ↔ recent). Only the list re-fetches — the map is order-agnostic.
  setListSort({ commit, dispatch, state }, sort) {
    const next = sort === "recent" ? "recent" : "relevance";
    if (next === state.list_sort) return Promise.resolve(null);
    commit("SET_LIST_SORT", next);
    commit("SET_COMPANY_PAGE", 1);
    return dispatch("fetchCountryDetail", { country_code: state.selected_country });
  },

  // Replace the stage selection (map tile / KPI drill = single, or null to clear).
  selectStage({ commit, dispatch, state }, slug) {
    commit("SET_SELECTED_STAGE", slug);
    commit("SET_COMPANY_PAGE", 1);
    // Refetch both — dashboard narrows the map chip count to the picked stage; country detail filters the list server-side.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: state.selected_country, stage_slugs: Array.isArray(slug) ? slug : slug ? [slug] : [] }),
    ]);
  },

  // Toggle one stage in/out of the multi-select set (rail chips + per-chip remove).
  toggleStage({ commit, dispatch, state }, slug) {
    const cur = Array.isArray(state.selected_stage_slugs) ? state.selected_stage_slugs : [];
    const next = cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug];
    commit("SET_SELECTED_STAGE", next);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: state.selected_country, stage_slugs: next }),
    ]);
  },

  addClientCountry({ commit, dispatch, state }, country_code) {
    if (!country_code) return Promise.resolve();
    commit("ADD_CLIENT_COUNTRY", country_code);
    commit("SET_COMPANY_PAGE", 1);
    // Funnel every pick through the multi-list (source of truth) so "Add market" reaches the URL and accumulates — append to the multi list, or the single slot as a 1-item set.
    const current = (Array.isArray(state.selected_country_list) && state.selected_country_list.length)
      ? state.selected_country_list
      : (state.selected_country ? [state.selected_country] : []);
    return dispatch("setSelectedCountries", [...current, country_code]);
  },

  removeClientCountry({ commit, state, dispatch, getters }, country_code) {
    commit("REMOVE_CLIENT_COUNTRY", country_code);
    // Drop from selected_country_list (source of truth) so the chip de-activates, map/list rescope, URL narrows — removing from client_added alone leaves it selected + in the URL.
    const list = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    if (list.includes(country_code)) {
      return dispatch("setSelectedCountries", list.filter((c) => c !== country_code));
    }
    // Fallback: code held only in the single slot (e.g. focusMarket) — pick the next default so the dashboard doesn't stick on stale data (null → global).
    if (state.selected_country === country_code) {
      const next = getters.defaultCountryCode;
      commit("SET_SELECTED_COUNTRY", next);
      // The comment above only came true once the dashboard actually refetched — a list-only
      // refetch left the map drawing the REMOVED market's counts under the new selection.
      return Promise.all([
        dispatch("fetchDashboard", {}).catch(() => {}),
        dispatch("fetchCountryDetail", { country_code: next || null }),
      ]);
    }
  },

  setMapMode({ commit }, mode) {
    commit("SET_MAP_MODE", mode);
  },

  // Flow direction is a slicer, not a scope — toggling it re-runs both dashboard + country detail with the new direction filter applied.
  setFlowDirection({ commit, dispatch }, dir) {
    commit("SET_FLOW_DIRECTION", dir);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // Empty/null = exit multi mode; refetches dashboard + country detail so map chips and list scope simultaneously.
  setSelectedCountries({ commit, dispatch, state }, codes) {
    // Focus change = new market card — drop the old aggregate so the card shows
    // its loading state, not the previous market's numbers under the new name.
    const next = Array.isArray(codes) ? codes.filter(Boolean).map((c) => String(c).toUpperCase()) : [];
    const next_focus = next.length === 1 ? next[0] : null;
    if (next_focus !== state.selected_country) commit("SET_COUNTRY_DETAIL", null);
    commit("SET_SELECTED_COUNTRY_LIST", codes);
    // Switching market drops any outbound corridor — it was scoped to the old origin.
    commit("SET_OUTGOING_TARGET", null);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  clearMultiCountry({ commit, dispatch }) {
    commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // One-way ↔ either changes the wire shape (froms/tos vs country_codes + direction:all) — BOTH surfaces must refetch or the list count goes silently stale (loading states key off these fetches).
  setRouteDirection({ commit, dispatch }, dir) {
    commit("SET_ROUTE_DIRECTION", dir);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // Canonical corridor escape hatch (toolbar ×, rail self-loop fixes, zero-result actions). Clearing To also nulls the focus mirror; an outbound refetch re-mirrors the single origin.
  clearRouteLeg({ commit, dispatch }, leg) {
    if (leg === "from") commit("SET_ROUTE_FROM", []);
    else commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_COMPANY_PAGE", 1);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  toggleCountry({ state, dispatch }, code) {
    if (!code) return Promise.resolve();
    const upper = String(code).toUpperCase();
    const current = Array.isArray(state.selected_country_list) ? state.selected_country_list : [];
    const origins = Array.isArray((state.rail_filters || {}).origins) ? state.rail_filters.origins : [];
    // Toggling the outbound focus itself is the chip's × — remove the From leg, don't re-add the market as a destination (that makes a self-corridor).
    if (current.length == 0 && origins.includes(upper) && state.selected_country === upper) {
      return dispatch("clearRouteLeg", "from");
    }
    const set = new Set(current);
    // Seed multi from the single selection, but skip the outbound focus mirror (selected_country ∈ origins) — it's the FROM leg; seeding it puts the same market on both route legs.
    if (current.length == 0 && state.selected_country && !origins.includes(state.selected_country)) set.add(state.selected_country);
    set.has(upper) ? set.delete(upper) : set.add(upper);
    return dispatch("setSelectedCountries", [...set]);
  },

  // Single canonical date-window reset (FiltersRail's Date chip ×) — keeps the rail from re-deriving the default in two places; refetches both so all counts reflect it.
  resetDateRange({ commit, dispatch }) {
    commit("SET_DATE_RANGE", { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS });
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
    ]);
  },

  // "Clear all" coalescer — dispatching the individual reset actions fires 3-5 racing fetches (each pays network + DB cost); commit all resets synchronously, then ONE refetch pair.
  bulkResetFilters({ commit, dispatch }) {
    // Must stay the SOLE dispatcher for "Clear all" — pairing it with other reset actions fires parallel fetches whose last-write-wins counts differ from fresh-load values (user-flagged).
    commit("SET_SELECTED_COUNTRY", null);
    commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_SELECTED_STAGE", null);
    // Must clear the map corridor too, or the cleared board stays silently scoped to it.
    commit("SET_OUTGOING_TARGET", null);
    commit("SET_ROUTE_DIRECTION", "either");
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.GLOBAL);
    commit("SET_DATE_RANGE", { from: null, to: null, window_days: DEFAULT_WINDOW_DAYS });
    commit("SET_RAIL_FILTERS", defaultRailFilters());
    // Drop the unscoped market snapshot so the next fetchDashboard repopulates it — keeps stale picks-narrowed data out of chip rendering before the new response arrives.
    commit("SET_DASHBOARD_MARKET_INDEX", null);
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: null }),
    ]);
  },

  // Natural-language Find: parse the sentence server-side, then apply the interpreted filters to the map + rail as one fresh search. Verticals arrive as free text → similarity-resolved on the fetch.
  async applyNlQuery({ commit, dispatch, rootState }, query) {
    const q = (query || "").trim();
    if (!q) return null;
    const profile_id = rootState.user?.currentId?.profile_id;
    commit("SET_NL_LOADING", true);
    try {
      const res = await getExpansionLookup({ profile_id, query: q, per_page: 1, is_include_metadata: false });
      // Interpreted filters live under `metadata` (canonical envelope); tolerate the interim `search` and flat shapes too.
      const f = (res && ((res.metadata && res.metadata.filters) || (res.search && res.search.filters) || res.filters)) || {};
      const arr = (v) => (Array.isArray(v) ? v : []);
      const codes = arr(f.country_codes).length ? arr(f.country_codes) : (f.country_code ? [f.country_code] : []);
      const stages = arr(f.stages);
      // Copy EVERY parsed filter the rail knows (shared key sets), not a subset — else e.g. technologies is dropped silently while hasResolvedFilter still counts it.
      const rail = { ...defaultRailFilters() };
      RAIL_FILTER_ARRAY_KEYS.forEach((k) => {
        if (Array.isArray(f[k]) && f[k].length) rail[k] = f[k];
      });
      // Rail state keeps its own names for the params renamed on the API: map the canonical response keys back onto them.
      if (Array.isArray(f.signal_types) && f.signal_types.length) rail.signal_type_slugs = f.signal_types;
      if (Array.isArray(f.locations) && f.locations.length) rail.origins = f.locations;
      RAIL_FILTER_SCALAR_KEYS.forEach((k) => {
        const n = Number(f[k]);
        if (f[k] != null && f[k] !== "" && Number.isFinite(n)) rail[k] = n;
      });
      RAIL_FILTER_BOOL_KEYS.forEach((k) => {
        if (f[k] === true) rail[k] = true;
      });
      // Commit all filter state synchronously, then ONE fetch pair (mirrors bulkResetFilters) so the parsed query lands as a single coherent search, not 3 racing ones.
      commit("SET_SELECTED_COUNTRY", null);
      commit("SET_SELECTED_COUNTRY_LIST", codes.length ? codes : null);
      commit("SET_OUTGOING_TARGET", null);
      commit("SET_SELECTED_STAGE", stages);
      commit("SET_FLOW_DIRECTION", f.direction || "all");
      commit("SET_RAIL_FILTERS", rail);
      commit("SET_COMPANY_PAGE", 1);
      // Multi/none → GLOBAL; COUNTRY only for one country (else it mis-gates the single-country empty-state chrome).
      commit("SET_MAP_MODE", codes.length === 1 ? MAP_MODE.COUNTRY : MAP_MODE.GLOBAL);
      await Promise.all([
        dispatch("fetchDashboard", {}).catch(() => {}),
        // Multi → country_code null (multi_codes drives the request; the race guard drops a mismatch). .catch so a fetch failure shows the fetch-error UI, not a false noMatch.
        dispatch("fetchCountryDetail", { country_code: codes.length === 1 ? codes[0] : null }).catch(() => {}),
      ]);
      return { filters: f, nl_note: res && res.nl_note, nl_note_code: res && res.nl_note_code };
    } catch (e) {
      return null;
    } finally {
      commit("SET_NL_LOADING", false);
    }
  },

  // Rail change handler: commits into Vuex (fetchCountryDetail reads one source of truth) and refetches; dedup-guarded so a same-shape emit (rail re-render) skips the refetch.
  applyRailFilters({ commit, dispatch, state }, filters) {
    const sortArr = (v) => (Array.isArray(v) ? [...v].sort() : []);
    const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
    const next = {
      directions:              sortArr(filters?.directions),
      freshness:               sortArr(filters?.freshness),
      scopes:                  sortArr(filters?.scopes),
      origins:                 sortArr(filters?.origins),
      vertical_categories:     sortArr(filters?.vertical_categories),
      vertical_sub_categories: sortArr(filters?.vertical_sub_categories),
      verticals:               sortArr(filters?.verticals),
      industries:              sortArr(filters?.industries),
      exclude_country_codes:   sortArr(filters?.exclude_country_codes),
      exclude_origins:         sortArr(filters?.exclude_origins),
      exclude_verticals:       sortArr(filters?.exclude_verticals),
      technologies:            sortArr(filters?.technologies),
      companies:               sortArr(filters?.companies),
      company_groups:          sortArr(filters?.company_groups),
      employee_slugs:          sortArr(filters?.employee_slugs),
      momentum:                sortArr(filters?.momentum),
      signal_strengths:        sortArr(filters?.signal_strengths),
      signal_type_slugs:       sortArr(filters?.signal_type_slugs),
      ahead_of_pace:    filters?.ahead_of_pace === true,
      only_contraction: filters?.only_contraction === true,
      employee_min: num(filters?.employee_min),
      employee_max: num(filters?.employee_max),
      founded_min:  num(filters?.founded_min),
      founded_max:  num(filters?.founded_max),
      revenue_min:  num(filters?.revenue_min),
      revenue_max:  num(filters?.revenue_max),
      min_markets:  num(filters?.min_markets),
      min_signal_count: num(filters?.min_signal_count),
    };
    // Source filters ride rail_filters so the fetch actions forward them; stored order-preserved because date sub-fields are positional [start, end].
    const SOURCE_KEYS = ["job_titles","job_locations","job_exclude_locations","job_posted_dates","advertisement_target_locations","advertisement_exclude_target_locations","advertisement_search_terms","advertisement_start_dates","advertisement_end_dates","news_categories","news_published_dates","keywords"];
    SOURCE_KEYS.forEach((k) => { next[k] = Array.isArray(filters?.[k]) ? [...filters[k]] : []; });
    // filter_conditions = [{key, operator}] for the source filters' AND/OR toggles.
    next.filter_conditions = Array.isArray(filters?.filter_conditions) ? filters.filter_conditions.map((c) => ({ ...c })) : [];
    const cur = state.rail_filters || {};
    const ARRAY_KEYS = ["directions","freshness","scopes","origins","vertical_categories","vertical_sub_categories","verticals","technologies","industries","exclude_country_codes","exclude_origins","exclude_verticals","companies","company_groups","employee_slugs","momentum","signal_strengths","signal_type_slugs"];
    const SCALAR_KEYS = ["ahead_of_pace","only_contraction","employee_min","employee_max","founded_min","founded_max","revenue_min","revenue_max","min_markets","min_signal_count"];
    const arraysSame = ARRAY_KEYS.every((k) => {
      const a = Array.isArray(cur[k]) ? [...cur[k]].sort() : [];
      return a.length === next[k].length && a.every((v, i) => v === next[k][i]);
    });
    const scalarsSame = SCALAR_KEYS.every((k) => (cur[k] == null ? null : cur[k]) === (next[k] == null ? null : next[k]));
    // Sorted compare just for change-detection (doesn't mutate next[k]); detects any add/remove/edit incl. date bounds.
    const sourceSame = SOURCE_KEYS.every((k) => {
      const a = Array.isArray(cur[k]) ? [...cur[k]].sort() : [];
      const b = [...next[k]].sort();
      return a.length === b.length && a.every((v, i) => v === b[i]);
    });
    // filter_conditions holds objects — JSON compare (order-insensitive by key) for change-detection.
    const fcKey = (fc) => JSON.stringify((Array.isArray(fc) ? fc : []).map((c) => [c.key, c.operator]).sort());
    const fcSame = fcKey(cur.filter_conditions) === fcKey(next.filter_conditions);
    if (arraysSame && scalarsSame && sourceSame && fcSame) return Promise.resolve(null);
    commit("SET_RAIL_FILTERS", next);
    commit("SET_COMPANY_PAGE", 1);
    // Dashboard + What's New refetch with the list so map count and fast-movers track the active filters.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", {}),
      dispatch("fetchFastMovers").catch(() => {}),
    ]);
  },

  // "Back to overview": clears country + stage, returns to global map mode, and fetches the global aggregate immediately so tiles + list repopulate instead of showing zeros / the empty state.
  clearSelection({ commit, dispatch }) {
    // SET_SELECTED_COUNTRY_LIST(null) clears both the multi-list and the single alias; also drop the corridor so "back to overview" leaves no stale chip.
    commit("SET_SELECTED_COUNTRY_LIST", null);
    commit("SET_OUTGOING_TARGET", null);
    commit("SET_SELECTED_STAGE", null);
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_MAP_MODE", MAP_MODE.GLOBAL);
    // Both surfaces, like every other scope-changing action. Refetching only the list left
    // `dashboard` holding the last market's scoped payload, and buildUnifiedGlobalView seeds
    // market_index first then lets dashboard.data overwrite it — so closing the Spain card
    // reset the chips/count pill to global (49,533) while the map kept Spain-scoped labels
    // (721 US, 64 Canada). Looked like the numbers vanished on close.
    return Promise.all([
      dispatch("fetchDashboard", {}).catch(() => {}),
      dispatch("fetchCountryDetail", { country_code: null }),
    ]);
  },

  // Global aggregate fetch for boots without a country in the URL — master data renders right away instead of forcing a country pick first.
  fetchGlobalDetail({ dispatch }) {
    return dispatch("fetchCountryDetail", { country_code: null });
  },

  // Saved Views backend = /saved/saved_searches (same as /search); search_type_id for `expansion` resolves at runtime because the id differs across environments.
  resolveSavedViewsTypeId({ commit, state }) {
    if (state.saved_views.search_type_id) return Promise.resolve(state.saved_views.search_type_id);
    if (_saved_search_type_id_promise) return _saved_search_type_id_promise;
    _saved_search_type_id_promise = getSavedSearchTypes()
      .then((res) => {
        const rows = (res && (res.data || res)) || [];
        const expansion = rows.find((r) => r && r.slug === "expansion");
        const id = expansion ? expansion.search_type_id : null;
        if (id != null) commit("SET_SAVED_VIEWS_TYPE_ID", id);
        return id;
      })
      .catch(() => null)
      .finally(() => { _saved_search_type_id_promise = null; });
    return _saved_search_type_id_promise;
  },

  async fetchSavedViews({ commit, dispatch, state, rootState }, { force = false } = {}) {
    if (!force) {
      if (state.saved_views.views.length) return state.saved_views.views;
      if (state.saved_views.loading) return state.saved_views.views;
      if (state.saved_views.fetch_attempted && state.saved_views.search_type_id) return state.saved_views.views;
    }
    const id = await dispatch("resolveSavedViewsTypeId");
    const id_num = Number(id);
    if (!Number.isFinite(id_num) || id_num <= 0) {
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
      return [];
    }
    // authorisationHandler reads `profile_id` from req.body for the Team_Profiles ownership JOIN — without it auth fails before body-validation (mirrors /search's getSaveList caller).
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) {
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
      return [];
    }
    commit("SET_SAVED_VIEWS_LOADING", true);
    try {
      const res = await getSaveList({ profile_id, search_type_id: id_num, order_by: "created_at", is_ascending_order: false });
      const list = (res && (res.data || res)) || [];
      commit("SET_SAVED_VIEWS", list);
      return list;
    } catch (e) {
      return [];
    } finally {
      commit("SET_SAVED_VIEWS_LOADING", false);
      commit("SET_SAVED_VIEWS_FETCH_ATTEMPTED", true);
    }
  },

  applySavedView({ commit, dispatch }, view) {
    if (!view) return Promise.resolve(null);
    const params = view.parameters || {};
    // Rail filters FIRST — SET_RAIL_FILTERS diffs origins to drop an orphaned focus; running it after the market focus lands wipes the just-restored selected_country.
    if (params.rail_filters) {
      commit("SET_RAIL_FILTERS", migrateLegacyRailFilters(params.rail_filters));
    }
    // Multi scope next: SET_SELECTED_COUNTRY_LIST clears selected_country, so single-mode views (list null) restore correctly because the multi branch is skipped.
    if (Array.isArray(params.selected_country_list) && params.selected_country_list.length > 0) {
      commit("SET_SELECTED_COUNTRY_LIST", params.selected_country_list);
    } else if (params.selected_country !== undefined) {
      commit("SET_SELECTED_COUNTRY", params.selected_country || null);
    }
    if (params.selected_stage_slugs !== undefined) {
      commit("SET_SELECTED_STAGE", params.selected_stage_slugs || []);
    } else if (params.selected_stage_slug !== undefined) {
      // Back-compat: saved views created before multi-select stored a single slug.
      commit("SET_SELECTED_STAGE", params.selected_stage_slug || null);
    }
    // Pre-route-model views open either-direction (decided 2026-07-22) — never inherit the prior view's direction.
    commit("SET_ROUTE_DIRECTION", params.route_direction === "one_way" ? "one_way" : "either");
    if (params.map_mode) {
      commit("SET_MAP_MODE", params.map_mode);
    }
    if (params.date_range) {
      commit("SET_DATE_RANGE", params.date_range);
    }
    commit("SET_COMPANY_PAGE", 1);
    commit("SET_ACTIVE_VIEW_ID", view.saved_search_id);
    return dispatch("fetchCountryDetail", {});
  },

  // `pinned_sections` rides in the saved view so teammates loading it see the same rail composition, not just the filter values.
  async createSavedView({ commit, dispatch, state, rootState }, { name, pinned_sections } = {}) {
    if (!name) return null;
    const id = await dispatch("resolveSavedViewsTypeId");
    if (!id) return null;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return null;
    const parameters = {
      selected_country:      state.selected_country,
      // Persist multi-market watchlist so restoring the view brings back the basket, not just a single country.
      selected_country_list: Array.isArray(state.selected_country_list) ? state.selected_country_list : null,
      selected_stage_slugs:  state.selected_stage_slugs,
      // Direction is part of the view — without it a saved "either" view restores as one-way, inheriting whatever was set before it.
      route_direction:       state.route_direction,
      map_mode:              state.map_mode,
      date_range:            state.date_range,
      rail_filters:          state.rail_filters,
      pinned_sections:       Array.isArray(pinned_sections) ? pinned_sections : [],
    };
    try {
      const res = await addSaveList({
        profile_id,
        saved_search_name: name,
        parameters,
        search_type_id: id,
        is_pinned: false,
      });
      const view = res && res.data ? res.data : null;
      if (view) {
        // /create returns a minimal row (no parameters echo) — hydrate locally so the just-saved view applies without a refetch round-trip.
        commit("UPSERT_SAVED_VIEW", { ...view, parameters });
        commit("SET_ACTIVE_VIEW_ID", view.saved_search_id);
      }
      return view;
    } catch (e) {
      return null;
    }
  },

  async deleteSavedView({ commit, rootState }, id) {
    if (!id) return false;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return false;
    try {
      // Backend takes `saved_searches` (plural array), not `saved_search_id`; profile_id required by authorisationHandler's Team_Profiles JOIN.
      await deleteSaveList({ profile_id, saved_searches: [id] });
      commit("REMOVE_SAVED_VIEW", id);
      return true;
    } catch (e) {
      return false;
    }
  },

  async renameSavedView({ commit, rootState }, { id, name }) {
    if (!id || !name) return false;
    const profile_id = rootState.user?.currentId?.profile_id;
    if (!profile_id) return false;
    try {
      const res = await updateSaveList({ profile_id, saved_searches: [id], saved_search_name: name });
      // /update returns an array of rows (parameterEnrichmentHandler output); create returns a single row — normalise.
      const rows = res && res.data;
      const updated = Array.isArray(rows) ? rows[0] : rows;
      if (updated) commit("UPSERT_SAVED_VIEW", { saved_search_id: id, name, ...updated });
      return true;
    } catch (e) {
      return false;
    }
  },

  clearActiveView({ commit }) {
    commit("SET_ACTIVE_VIEW_ID", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
